import { useMemo } from "react";
import { AlertTriangle, MapPin, X } from "lucide-react";
import { useUserContext } from "../context/UserContext";
import { useInvalidStores } from "../hooks/useInvalidStores";
import { StoresFilter } from "./filtertypes";
import {
  getUserStoresForRetailer,
  type RetailerType,
} from "../config/retailers";
import { effectiveStoreSlotLimit } from "../utils/userUtils";
import { getRetailerStoreLimit } from "../utils/retailerStoreLimits";
import { retailerDisplayLabel } from "../utils/invalidStores";
import { useStores } from "../hooks/useStores";

function RetailerStoreFixer({
  retailer,
  invalidIds,
  userRetailers,
  allowMoreStores,
}: {
  retailer: RetailerType;
  invalidIds: string[];
  userRetailers: string[];
  allowMoreStores?: boolean;
}) {
  const { user } = useUserContext();
  const { stores } = useStores(retailer);
  const selectedStores = useMemo(
    () => getUserStoresForRetailer(user, retailer),
    [user, retailer]
  );
  const filled = selectedStores.filter(Boolean).length;
  const planLimit = getRetailerStoreLimit(userRetailers, retailer);
  const storeLimit = effectiveStoreSlotLimit(
    // The plan's slots; a retailer without a plan limit keeps what is saved.
    planLimit > 0 ? planLimit : Math.max(filled, 1),
    filled,
    allowMoreStores
  );

  return (
    <div className="rounded-2xl border border-amber-200 bg-amber-50/60 p-4 dark:border-amber-900/40 dark:bg-amber-950/20">
      <div className="mb-3">
        <p className="text-sm font-semibold text-gray-900 dark:text-white">
          {retailerDisplayLabel(retailer)}
        </p>
        <p className="mt-1 text-xs text-amber-800 dark:text-amber-200">
          Unavailable store{invalidIds.length === 1 ? "" : "s"}: {invalidIds.join(", ")}
        </p>
      </div>
      {storeLimit > 0 && (
        <StoresFilter
          selectedStores={selectedStores}
          storeLimit={storeLimit}
          retailer={retailer}
          loadedStores={stores as any}
        />
      )}
    </div>
  );
}

export default function InvalidStoresModal() {
  const { user } = useUserContext();
  const { invalidByRetailer, loading, shouldShowModal, dismiss } = useInvalidStores(user);

  if (!user || loading || !shouldShowModal) return null;

  const retailers = Object.keys(invalidByRetailer) as RetailerType[];
  if (retailers.length === 0) return null;

  const handleClose = () => {
    dismiss();
  };

  return (
    <div className="fixed inset-0 z-[10001] flex items-end justify-center bg-black/50 p-4 sm:items-center sheet-backdrop-in">
      <div className="flex max-h-[90vh] w-full max-w-lg flex-col overflow-hidden rounded-2xl bg-white shadow-xl dark:bg-dark-800 sheet-panel-in-sm">
        <div className="flex items-start justify-between border-b border-gray-200 px-5 py-4 dark:border-dark-600">
          <div className="flex items-start gap-3">
            <div className="mt-0.5 flex h-10 w-10 items-center justify-center rounded-full bg-amber-100 dark:bg-amber-900/30">
              <AlertTriangle className="h-5 w-5 text-amber-600 dark:text-amber-400" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                Update your stores
              </h2>
              <p className="mt-1 text-sm text-gray-600 dark:text-dark-300">
                Some saved store locations are no longer available. Pick new ones so alerts keep working.
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={handleClose}
            className="rounded-lg p-1.5 text-gray-400 hover:bg-gray-100 hover:text-gray-600 dark:hover:bg-dark-700"
            aria-label="Close"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="space-y-4 overflow-y-auto px-5 py-4">
          {retailers.map((retailer) => (
            <RetailerStoreFixer
              key={retailer}
              retailer={retailer}
              invalidIds={invalidByRetailer[retailer] || []}
              userRetailers={user.retailers || []}
              allowMoreStores={user.allowMoreStores}
            />
          ))}
        </div>

        <div className="flex items-center justify-between gap-3 border-t border-gray-200 px-5 py-4 dark:border-dark-600">
          <div className="flex items-center gap-2 text-xs text-gray-500 dark:text-dark-400">
            <MapPin className="h-4 w-4" />
            Changes save as you pick stores
          </div>
          <button
            type="button"
            onClick={handleClose}
            className="rounded-xl px-4 py-2 text-sm font-medium text-gray-600 hover:bg-gray-100 dark:text-dark-300 dark:hover:bg-dark-700"
          >
            Remind me later
          </button>
        </div>
      </div>
    </div>
  );
}
